import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { featAbilityPreview, featEligibility, featKey, featRules, validateFeatAbilities } from '../../../shared/rules/featSelection.mjs';
import { SKILLS } from '../skillSchema';
import { buildFeatEntry, featSkillChoice, withAbilityChoices } from '../utils/featEntry';

/**
 * The feat a level-up grants (Ability Score Improvement levels and Epic Boon), chosen in the level-up window. Offers the
 * feats the character qualifies for at the new level, with their ability and skill picks; "Decide later" leaves it
 * for the Feats window. Each change reports { entry, definition, ready, reason } (or null) through onChange.
 */
export default function LevelUpFeatChoice({ form, nextForm, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [choice, setChoice] = useState({ name: '', abilities: {}, skills: [], spellList: '' });
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await apiFetch(`/feats${form?.campaign ? `?campaign=${encodeURIComponent(form.campaign)}` : ''}`);
        const records = response?.ok ? await response.json() : [];
        if (live) setCatalog((records || []).map(featRules));
      } catch { if (live) setCatalog([]); }
    })();
    return () => { live = false; };
  }, [form?.campaign]);
  const options = useMemo(() => catalog.filter((item) => {
    const owned = (form?.feat || []).some((feat) => featKey(feat) === featKey(item));
    return featEligibility(nextForm, item).allowed && !(owned && !featRules(item).repeatable);
  }).sort((a, b) => a.featName.localeCompare(b.featName)), [catalog, form?.feat, nextForm]);
  const definition = options.find((item) => item.featName === choice.name) || null;
  const skillChoice = featSkillChoice(definition);
  const preview = definition ? featAbilityPreview(nextForm, definition, choice.abilities) : null;
  const signed = value => (value < 0 ? `−${Math.abs(value)}` : `+${value}`);
  const owned = new Set(Object.entries(form?.skills || {}).filter(([, value]) => value?.proficient).map(([key]) => key));

  // The feat entry and whether it is complete, for a given set of picks.
  const describe = (next) => {
    const feat = options.find((item) => item.featName === next.name);
    if (!feat) return null;
    const picks = featSkillChoice(feat);
    let entry = withAbilityChoices(buildFeatEntry(feat, (form?.feat || []).find((item) => item.featName === feat.featName)), feat, next.abilities);
    if (picks.has) entry = { ...entry, skills: { ...(feat.skills || {}), ...Object.fromEntries(next.skills.map((key) => [key, { proficient: true }])) } };
    if (featKey(feat) === 'magic initiate') entry = { ...entry, spellList: next.spellList };
    const done = Object.values(next.abilities).filter(Boolean).length === (feat.abilityIncreaseOptions?.length || 0)
      && (!picks.has || next.skills.length === picks.limit) && (featKey(feat) !== 'magic initiate' || Boolean(next.spellList));
    const reason = done ? validateFeatAbilities(nextForm, feat, entry) : 'Finish the feat’s choices.';
    return { entry, definition: feat, ready: Boolean(done && !reason), reason: reason || '' };
  };
  const update = (patch) => {
    const next = { ...choice, ...patch };
    setChoice(next);
    onChange?.(describe(next));
  };

  return (
    <div className="levelup-choice" role="group" aria-label="Feat">
      <span className="form-label">Feat</span>
      <p className="levelup-choice__hint">You gain a feat at this level. Choose it now, or decide later in Feats.</p>
      <Form.Group className="mb-2" controlId="levelup-feat">
        <Form.Label>Feat</Form.Label>
        <Form.Select value={choice.name} onChange={(event) => update({ name: event.target.value, abilities: {}, skills: [], spellList: '' })}>
          <option value="">Decide later</option>
          {options.map((item) => <option key={item.featName} value={item.featName}>{item.featName}</option>)}
        </Form.Select>
      </Form.Group>
      {definition?.notes && <p className="levelup-choice__hint">{definition.notes}</p>}
      {preview?.options.map((abilities, index) => (
        <Form.Group className="mb-2" controlId={`levelup-feat-ability-${index}`} key={index}>
          <Form.Label>{`Ability increase ${index + 1}`}</Form.Label>
          <Form.Select value={choice.abilities[index] || ''} onChange={(event) => update({ abilities: { ...choice.abilities, [index]: event.target.value } })}>
            <option value="" disabled>Select ability</option>
            {abilities.map(({ ability, from, to, atMaximum }) => <option key={ability} value={ability} disabled={atMaximum}>
              {atMaximum ? `${ability.toUpperCase()} · ${from} (maximum)` : `${ability.toUpperCase()} · ${from} → ${to}`}
            </option>)}
          </Form.Select>
        </Form.Group>
      ))}
      {preview?.result.length > 0 && (
        <div className="levelup-choice__result" role="status" aria-label="Ability score result">
          {preview.result.map(({ ability, from, to, fromModifier, toModifier }) => <span key={ability} className="levelup-choice__result-item">
            <strong>{ability.toUpperCase()} {from} → {to}</strong> {fromModifier === toModifier ? `· modifier stays ${signed(toModifier)}` : `· modifier ${signed(fromModifier)} → ${signed(toModifier)}`}
          </span>)}
        </div>
      )}
      {skillChoice.has && (
        <Form.Group className="mb-2" controlId="levelup-feat-skills">
          <Form.Label>{`Skill proficiencies (choose ${skillChoice.limit})`}</Form.Label>
          <Form.Select multiple value={choice.skills} onChange={(event) => update({ skills: Array.from(event.target.selectedOptions).map((option) => option.value).slice(0, skillChoice.limit) })}>
            {SKILLS.map(({ key, label }) => <option key={key} value={key} disabled={owned.has(key) || Boolean(definition.skills?.[key])}>{label}</option>)}
          </Form.Select>
        </Form.Group>
      )}
      {featKey(definition) === 'magic initiate' && (
        <Form.Group className="mb-2" controlId="levelup-feat-spell-list">
          <Form.Label>Spell list</Form.Label>
          <Form.Select value={choice.spellList} onChange={(event) => update({ spellList: event.target.value })}>
            <option value="" disabled>Choose a spell list</option>
            {['cleric', 'druid', 'wizard'].map((list) => <option key={list} value={list}>{list}</option>)}
          </Form.Select>
        </Form.Group>
      )}
    </div>
  );
}
